/**
 * useNodeDragInteraction hook
 *
 * Handles drag-to-move interactions for node editing. When a user presses
 * down on a node handle, drags it to a new position, and releases, this hook
 * updates the vertex position in real-time (preview) during drag and commits
 * the final position to Redux on pointer up.
 *
 * **Validates: Requirement 16.4** – WHEN a Registered_User drags a node,
 * THE Geometry_Editor SHALL update the feature geometry in real-time.
 */

import { useEffect, useCallback, useRef } from 'react';
import { toLonLat } from 'ol/proj';
import { fromLonLat } from 'ol/proj';
import type OLMap from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type OLFeature from 'ol/Feature';
import type Point from 'ol/geom/Point';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  selectIsEditingGeometry,
  selectEditingFeatureId,
  selectEditingLayerId,
  selectNode,
} from '../store/slices/geometryEditingSlice';
import { updateFeatureGeometryAsync } from '../store/slices/layersSlice';
import { moveNode, type Geometry, type Coordinate } from '../services/GeometryEditor';
import { useGeometryPreview, type GeometryPreviewControls } from './useGeometryPreview';
import { validateGeometry, type ValidatableGeometry } from '../utils/validateGeometry';

/**
 * Options for the useNodeDragInteraction hook.
 */
export interface UseNodeDragInteractionOptions {
  /** Live map value; preferred so restored editing binds after map initialization. */
  map?: OLMap | null;
  /** Reference retained for geometry preview helpers and existing consumers. */
  mapRef: React.RefObject<OLMap | null>;
  /**
   * Hit tolerance in pixels for detecting pointer on a node feature.
   * Default: 10
   */
  hitTolerance?: number;
  /**
   * Optional callback invoked when geometry validation fails after a node move.
   * The edit is still committed but the user is warned about topology issues.
   */
  onValidationWarning?: (errors: string[]) => void;
}

/**
 * Internal state tracked during a drag operation.
 */
interface DragState {
  /** Whether a drag is currently in progress */
  isDragging: boolean;
  /** The node index being dragged */
  nodeIndex: number;
  /** The OL feature being dragged (for real-time preview) */
  feature: OLFeature<Point> | null;
}

/**
 * useNodeDragInteraction
 *
 * A React hook that handles dragging node handles to move vertices in a geometry.
 *
 * Behavior:
 * - Only active when `isEditingGeometry` is true in Redux state
 * - On pointerdown: detects if the pointer is on a node feature using OL hit detection
 * - During pointermove: updates the node feature position for real-time preview
 *   and shows a dashed geometry outline preview via useGeometryPreview
 * - On pointerup: calls `moveNode()` from GeometryEditor service and dispatches
 *   the final geometry to Redux via `updateFeatureGeometry`
 * - Selects the dragged node via `selectNode` action
 * - Prevents map panning while dragging a node
 */
export function useNodeDragInteraction({
  map,
  mapRef,
  hitTolerance = 10,
  onValidationWarning,
}: UseNodeDragInteractionOptions): void {
  const dispatch = useAppDispatch();
  const isEditing = useAppSelector(selectIsEditingGeometry);
  const editingFeatureId = useAppSelector(selectEditingFeatureId);
  const editingLayerId = useAppSelector(selectEditingLayerId);
  const layers = useAppSelector((state) => state.layers.layers);

  // Geometry preview controls for showing dashed outline during drag
  const { showPreview, hidePreview } = useGeometryPreview({ mapRef });

  // Use refs to avoid stale closures in event handlers
  const isEditingRef = useRef(isEditing);
  const editingFeatureIdRef = useRef(editingFeatureId);
  const editingLayerIdRef = useRef(editingLayerId);
  const layersRef = useRef(layers);
  const showPreviewRef = useRef(showPreview);
  const hidePreviewRef = useRef(hidePreview);
  const onValidationWarningRef = useRef(onValidationWarning);
  const dragStateRef = useRef<DragState>({
    isDragging: false,
    nodeIndex: -1,
    feature: null,
  });

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    editingFeatureIdRef.current = editingFeatureId;
  }, [editingFeatureId]);

  useEffect(() => {
    editingLayerIdRef.current = editingLayerId;
  }, [editingLayerId]);

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    showPreviewRef.current = showPreview;
  }, [showPreview]);

  useEffect(() => {
    hidePreviewRef.current = hidePreview;
  }, [hidePreview]);

  useEffect(() => {
    onValidationWarningRef.current = onValidationWarning;
  }, [onValidationWarning]);

  /**
   * Retrieves the geometry of the feature currently being edited.
   */
  const getEditingGeometry = useCallback((): Geometry | null => {
    const featureId = editingFeatureIdRef.current;
    const layerId = editingLayerIdRef.current;
    if (!featureId || !layerId) return null;

    const layer = layersRef.current.find((l) => l.id === layerId);
    if (!layer || !layer.features) return null;

    const feature = layer.features.find((f) => f.id === featureId);
    if (!feature || !feature.geometry) return null;

    return feature.geometry as unknown as Geometry;
  }, []);

  /**
   * Checks if a node feature exists at the given pixel using OL hit detection.
   * Returns the node feature and its nodeIndex if found, null otherwise.
   */
  const getNodeFeatureAtPixel = useCallback(
    (
      pixel: [number, number],
      map: OLMap
    ): { feature: OLFeature<Point>; nodeIndex: number } | null => {
      let result: { feature: OLFeature<Point>; nodeIndex: number } | null = null;

      map.forEachFeatureAtPixel(
        pixel,
        (feature, layer) => {
          // Check if this feature is a node handle (has nodeIndex property)
          const nodeIndex = feature.get('nodeIndex');
          if (nodeIndex !== undefined && nodeIndex !== null) {
            result = { feature: feature as OLFeature<Point>, nodeIndex: nodeIndex as number };
            return true; // Stop iteration
          }
          return false;
        },
        { hitTolerance }
      );

      return result;
    },
    [hitTolerance]
  );

  /**
   * Handles pointerdown: starts a drag if the pointer is on a node feature.
   */
  const handlePointerDown = useCallback(
    (event: MapBrowserEvent<UIEvent>) => {
      if (!isEditingRef.current) return;

      const map = mapRef.current;
      if (!map) return;

      const pixel = event.pixel as [number, number];
      const nodeHit = getNodeFeatureAtPixel(pixel, map);

      if (!nodeHit) return;

      // Start dragging
      dragStateRef.current = {
        isDragging: true,
        nodeIndex: nodeHit.nodeIndex,
        feature: nodeHit.feature,
      };

      // Select the node being dragged
      dispatch(selectNode(nodeHit.nodeIndex));

      // Prevent default map interactions (panning)
      event.preventDefault();

      return false; // Signal to OL to stop event propagation
    },
    [dispatch, getNodeFeatureAtPixel, mapRef]
  );

  /**
   * Handles pointermove: updates the node feature position for real-time preview
   * and shows the geometry outline preview via useGeometryPreview.
   */
  const handlePointerMove = useCallback(
    (event: MapBrowserEvent<UIEvent>) => {
      const dragState = dragStateRef.current;
      if (!dragState.isDragging || !dragState.feature) return;

      // Update the node feature's geometry for real-time visual preview
      const mapCoordinate = event.coordinate;
      const geometry = dragState.feature.getGeometry();
      if (geometry) {
        geometry.setCoordinates(mapCoordinate);
      }

      // Show the geometry outline preview with the node at the new position
      const editingGeometry = getEditingGeometry();
      if (editingGeometry) {
        const lonLat = toLonLat(mapCoordinate) as Coordinate;
        showPreviewRef.current(editingGeometry, dragState.nodeIndex, lonLat);
      }

      // Prevent map panning during drag
      event.preventDefault();
    },
    [getEditingGeometry]
  );

  /**
   * Handles pointerup: commits the final node position to Redux and hides the preview.
   */
  const handlePointerUp = useCallback(
    (event: MapBrowserEvent<UIEvent>) => {
      const dragState = dragStateRef.current;
      if (!dragState.isDragging) return;

      const featureId = editingFeatureIdRef.current;
      const layerId = editingLayerIdRef.current;
      if (!featureId || !layerId) {
        // Reset drag state and hide preview
        dragStateRef.current = { isDragging: false, nodeIndex: -1, feature: null };
        hidePreviewRef.current();
        return;
      }

      const editingGeometry = getEditingGeometry();
      if (!editingGeometry) {
        dragStateRef.current = { isDragging: false, nodeIndex: -1, feature: null };
        hidePreviewRef.current();
        return;
      }

      // Convert the final map coordinate to geographic coordinates (lon/lat)
      const mapCoordinate = event.coordinate;
      const lonLat = toLonLat(mapCoordinate) as Coordinate;

      // Call moveNode from GeometryEditor service
      const result = moveNode(editingGeometry, dragState.nodeIndex, lonLat);

      if (result.success && result.geometry) {
        // Validate the resulting geometry topology
        const validation = validateGeometry(result.geometry as unknown as ValidatableGeometry);
        if (!validation.valid && onValidationWarningRef.current) {
          onValidationWarningRef.current(validation.errors);
        }

        // Optimistically update Redux and persist; the thunk rolls back on failure.
        dispatch(
          updateFeatureGeometryAsync({
            layerId,
            featureId,
            geometry: result.geometry as unknown as GeoJSON.Geometry,
            previousGeometry: JSON.parse(JSON.stringify(editingGeometry)) as GeoJSON.Geometry,
          })
        );
      }

      // Hide the geometry preview and reset drag state
      hidePreviewRef.current();
      dragStateRef.current = { isDragging: false, nodeIndex: -1, feature: null };
    },
    [dispatch, getEditingGeometry]
  );

  /**
   * Register/unregister pointer event listeners on the map.
   */
  useEffect(() => {
    const liveMap = map ?? mapRef.current;
    if (!liveMap) return;

    if (!isEditing) return;

    liveMap.on('pointerdown', handlePointerDown as any);
    liveMap.on('pointermove', handlePointerMove as any);
    liveMap.on('pointerup', handlePointerUp as any);

    return () => {
      liveMap.un('pointerdown', handlePointerDown as any);
      liveMap.un('pointermove', handlePointerMove as any);
      liveMap.un('pointerup', handlePointerUp as any);

      // Reset drag state on cleanup
      dragStateRef.current = { isDragging: false, nodeIndex: -1, feature: null };
    };
  }, [map, mapRef, isEditing, handlePointerDown, handlePointerMove, handlePointerUp]);
}

export default useNodeDragInteraction;
